/**
 * @file configurationEditor.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Wednesday, 9th December 2020 12:51:43 pm
 * @copyright 2015 - 2020 SKAT LLC, Delive LLC
 * @flow
 */
import type { iStockExtension, iWindowManager, iPageManager, iShortcutsManager, iPreferencesManager } from '../types'

import { __, KEYBINDING_SCOPE } from '../globals'
import Configuration from '../views/Configuration'
import * as React from 'react'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faInfoCircle, faCog } from '@fortawesome/free-solid-svg-icons'
import { Inject } from '../serviceLocator'
import localforage from 'localforage'

library.add(faInfoCircle, faCog)

class ConfigurationEditorExtension implements iStockExtension {
  @Inject windowManager : iWindowManager
  @Inject pageManager : iPageManager
  @Inject shortcutsManager : iShortcutsManager
  @Inject preferencesManager : iPreferencesManager

  settingsManagerWindowId: symbol

  async activate () : Promise<void> {
    this.settingsManagerWindowId = Symbol('id')

    this.shortcutsManager.register({
      name: 'Configuration',
      title: __('SHOW_CONFIGURATION_WINDOW'),
      scope: [KEYBINDING_SCOPE.GLOBAL, null],
      defaultShortcut: 'o s enter',
      action: this.invokeConfigurationWindow
    })
  }

  async clearMapCache () : Promise<void> {
    try {
      await fetch('/clear_map_cache')
    } catch (e) {
      console.error('Cache clear failed', e)
    }
  }

  async clearDataCache () : Promise<void> {
    await localforage.clear()
  }

  invokeConfigurationWindow : Function = async () => {
    await this.windowManager.show({
      id: this.settingsManagerWindowId,
      name: 'configuration',
      label: __('CONFIGURATION'),
      title: 'Configuration',
      testName: 'configuration-window',
      icon: 'cog',
      width: 800,
      view: <Configuration />,
      buttons: [{
        name: 'clear-data-cache',
        label: __('CLEAR_DATA_CACHE'),
        title: __('CLEAR_DATA_CACHE'),
        action: async () => {
          await this.clearDataCache()
          this.windowManager.close(this.settingsManagerWindowId)
        }
      }, {
        name: 'clear-map-cache',
        label: __('CLEAR_MAP_CACHE'),
        title: __('CLEAR_MAP_CACHE'),
        action: async () => {
          await this.clearMapCache()
          this.windowManager.close(this.settingsManagerWindowId)
        }
      }, {
        name: 'close',
        label: __('CLOSE'),
        title: __('CLOSE_TOOLTIP'),
        action: async () => this.windowManager.close(this.settingsManagerWindowId)
      }]
    })
    this.windowManager.activate(this.settingsManagerWindowId)

    this.shortcutsManager.register({
      name: 'Close settings',
      title: 'Close current settings window',
      scope: [KEYBINDING_SCOPE.GLOBAL, null],
      defaultShortcut: 'esc',
      action: () => this.windowManager.close(this.settingsManagerWindowId)
    })
  }
}

export default ConfigurationEditorExtension
